// Procedural audio using Web Audio API.
// Avoids dependence on external audio files which can fail behind ingress / CORS.

let ctx = null;
let masterGain = null;
let musicTimer = null;
let musicEnabled = true;

function getCtx() {
  if (!ctx) {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return null;
    ctx = new AC();
    masterGain = ctx.createGain();
    masterGain.gain.value = 0.4;
    masterGain.connect(ctx.destination);
  }
  if (ctx.state === "suspended") ctx.resume();
  return ctx;
}

function midiToFreq(midi) {
  return 440 * Math.pow(2, (midi - 69) / 12);
}

function playTone({ freq, duration = 0.2, type = "sine", volume = 0.3, attack = 0.01, release = 0.1 }) {
  const c = getCtx();
  if (!c) return;
  const osc = c.createOscillator();
  const g = c.createGain();
  osc.type = type;
  osc.frequency.value = freq;
  const t = c.currentTime;
  g.gain.setValueAtTime(0, t);
  g.gain.linearRampToValueAtTime(volume, t + attack);
  g.gain.linearRampToValueAtTime(0, t + duration + release);
  osc.connect(g).connect(masterGain);
  osc.start(t);
  osc.stop(t + duration + release + 0.05);
}

function playNoise({ duration = 0.2, volume = 0.3, filterFreq = 2000 }) {
  const c = getCtx();
  if (!c) return;
  const buffer = c.createBuffer(1, c.sampleRate * duration, c.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 2 - 1) * 0.6;
  const src = c.createBufferSource();
  src.buffer = buffer;
  const filter = c.createBiquadFilter();
  filter.type = "lowpass";
  filter.frequency.value = filterFreq;
  const g = c.createGain();
  g.gain.value = volume;
  src.connect(filter).connect(g).connect(masterGain);
  src.start();
  src.stop(c.currentTime + duration);
}

// ===== Sound effects =====
export const SFX = {
  jump: () => playTone({ freq: 520, duration: 0.08, type: "square", volume: 0.18 }),
  doubleJump: () => {
    playTone({ freq: 600, duration: 0.07, type: "square", volume: 0.18 });
    setTimeout(() => playTone({ freq: 800, duration: 0.07, type: "square", volume: 0.18 }), 70);
  },
  eat: () => {
    playTone({ freq: 700, duration: 0.06, type: "triangle", volume: 0.2 });
    setTimeout(() => playTone({ freq: 900, duration: 0.06, type: "triangle", volume: 0.2 }), 60);
  },
  drink: () => {
    playTone({ freq: 400, duration: 0.06, type: "sine", volume: 0.18 });
    setTimeout(() => playTone({ freq: 320, duration: 0.08, type: "sine", volume: 0.18 }), 50);
  },
  roar: () => {
    const c = getCtx();
    if (!c) return;
    const osc = c.createOscillator();
    const g = c.createGain();
    osc.type = "sawtooth";
    osc.frequency.setValueAtTime(140, c.currentTime);
    osc.frequency.exponentialRampToValueAtTime(60, c.currentTime + 0.5);
    g.gain.setValueAtTime(0.0001, c.currentTime);
    g.gain.linearRampToValueAtTime(0.5, c.currentTime + 0.05);
    g.gain.exponentialRampToValueAtTime(0.001, c.currentTime + 0.6);
    osc.connect(g).connect(masterGain);
    osc.start();
    osc.stop(c.currentTime + 0.7);
    playNoise({ duration: 0.5, volume: 0.25, filterFreq: 800 });
  },
  hit: () => {
    playTone({ freq: 200, duration: 0.12, type: "square", volume: 0.3 });
    playNoise({ duration: 0.15, volume: 0.25, filterFreq: 600 });
  },
  win: () => {
    [60, 64, 67, 72].forEach((m, i) =>
      setTimeout(
        () => playTone({ freq: midiToFreq(m), duration: 0.18, type: "triangle", volume: 0.3 }),
        i * 110,
      ),
    );
  },
  lose: () => {
    [67, 64, 60, 55].forEach((m, i) =>
      setTimeout(
        () => playTone({ freq: midiToFreq(m), duration: 0.2, type: "sawtooth", volume: 0.25 }),
        i * 140,
      ),
    );
  },
  star: () =>
    [72, 76, 79].forEach((m, i) =>
      setTimeout(
        () => playTone({ freq: midiToFreq(m), duration: 0.1, type: "triangle", volume: 0.25 }),
        i * 80,
      ),
    ),
  click: () => playTone({ freq: 600, duration: 0.04, type: "square", volume: 0.15 }),
  backflip: () => {
    [72, 76, 79, 84].forEach((m, i) =>
      setTimeout(
        () => playTone({ freq: midiToFreq(m), duration: 0.06, type: "triangle", volume: 0.18 }),
        i * 50,
      ),
    );
  },
};

// ===== Music =====
const INSTRUMENT_TYPES = {
  marimba: { osc: "triangle", attack: 0.005, release: 0.18 },
  flute: { osc: "sine", attack: 0.04, release: 0.12 },
  bells: { osc: "triangle", attack: 0.002, release: 0.4 },
  synth: { osc: "square", attack: 0.01, release: 0.08 },
};

export function startMusic(levelMusic) {
  stopMusic();
  if (!levelMusic || !musicEnabled) return;
  const c = getCtx();
  if (!c) return;
  const { scale, tempo, instrument } = levelMusic;
  const inst = INSTRUMENT_TYPES[instrument] || INSTRUMENT_TYPES.marimba;
  const beatMs = 60000 / tempo;
  const baseMidi = 60; // C4
  let step = 0;
  const pattern = [0, 2, 4, 2, 0, 4, 2, -1, 0, 2, 4, 7, 4, 2, 0, -1];
  const schedule = () => {
    const idx = pattern[step % pattern.length];
    if (idx >= 0) {
      const note = scale[idx % scale.length] + Math.floor(idx / scale.length) * 12;
      const freq = midiToFreq(baseMidi + note);
      playTone({
        freq,
        duration: 0.18,
        type: inst.osc,
        volume: 0.15,
        attack: inst.attack,
        release: inst.release,
      });
      // Add a soft bass note every 4th step
      if (step % 4 === 0) {
        playTone({
          freq: midiToFreq(baseMidi + scale[0] - 12),
          duration: 0.3,
          type: "sine",
          volume: 0.12,
          attack: 0.02,
          release: 0.2,
        });
      }
    }
    step++;
  };
  schedule();
  musicTimer = setInterval(schedule, beatMs / 2);
}

export function stopMusic() {
  if (musicTimer) {
    clearInterval(musicTimer);
    musicTimer = null;
  }
}

export function setMusicEnabled(enabled) {
  musicEnabled = enabled;
  if (!enabled) stopMusic();
}

export function isMusicEnabled() {
  return musicEnabled;
}

// Initialise audio context on first user interaction (browsers block otherwise).
export function unlockAudio() {
  getCtx();
}
